import { ThemeProvider } from '@repo/ui/components/theme-provider'
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/ui/alert'
import { Button } from '@repo/ui/components/ui/button'
import { Card, CardContent } from '@repo/ui/components/ui/card'
import { QueryClientProvider } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { themeConfig } from '@/config'
import { createQueryClient } from '@/lib/config/query-client'
import { createTrpcClient } from '@/lib/config/trpc-client'
import { PostHogProviderWrapper } from '@/lib/posthog'
import { SentryErrorBoundary, SentryProvider } from '@/lib/sentry'
import { trpc } from '@/lib/trpc'

export function Providers({ children }: { children: ReactNode }) {
  const [queryClient] = useState(() => createQueryClient())
  const [trpcClient] = useState(() => createTrpcClient())

  return (
    <SentryErrorBoundary
      fallback={({ error, resetError }) => {
        const message = error instanceof Error ? error.message : 'Unknown error'

        return (
          <div className="flex min-h-screen items-center justify-center bg-background px-4">
            <Card className="w-full max-w-lg">
              <CardContent className="space-y-4 py-6">
                <Alert variant="destructive">
                  <AlertTitle>Something went wrong</AlertTitle>
                  <AlertDescription>We've been notified and will look into it.</AlertDescription>
                </Alert>
                <details className="text-left text-sm">
                  <summary className="cursor-pointer font-medium">Error details</summary>
                  <pre className="mt-2 overflow-auto rounded bg-muted p-4 text-xs">{message}</pre>
                </details>
                <Button
                  type="button"
                  onClick={() => {
                    resetError()
                    window.location.reload()
                  }}
                  className="w-full"
                >
                  Reload page
                </Button>
              </CardContent>
            </Card>
          </div>
        )
      }}
    >
      <SentryProvider>
        <QueryClientProvider client={queryClient}>
          <trpc.Provider client={trpcClient} queryClient={queryClient}>
            <PostHogProviderWrapper>
              <ThemeProvider defaultTheme={themeConfig.defaultTheme}>{children}</ThemeProvider>
            </PostHogProviderWrapper>
          </trpc.Provider>
        </QueryClientProvider>
      </SentryProvider>
    </SentryErrorBoundary>
  )
}
